// ============================================================
// Templates de Fluxo de Conversa por nicho
// ------------------------------------------------------------
// Para adicionar um novo template, basta inserir um novo objeto
// no array TEMPLATES abaixo com:
//   - key: id único (usado no estado da UI)
//   - nome: título exibido no card
//   - descricao: texto de apoio no card
//   - icone: emoji / ícone visual
//   - nodes: array de nós ReactFlow {id,type,position:{x,y},data:{defaults}}
//   - edges: array de arestas {id,source,target,sourceHandle?}
//
// Regras:
//   * Usar SOMENTE `type` existente em NODE_CATALOG (FlowBuilder/catalog.jsx)
//   * Cada template DEVE ter exatamente 1 nó com type="trigger"
//   * Posições: espaçamento mínimo ~340px horizontal / ~200px vertical
//   * sourceHandle nas arestas:
//       - menu/buttons/list: "opt-1","opt-2",... e fallback para saída padrão inválida
//       - randomizer: "a" / "b"
//       - condition: "true" / "false"
//       - demais: sem sourceHandle (saída padrão)
// ============================================================

// ------------------------------------------------------------
// 1) TEMPLATE BASE: Em branco (comportamento padrão atual)
// ------------------------------------------------------------
const TEMPLATE_BRANCO = {
  key: "branco",
  nome: "Em branco",
  descricao: "Comece do zero com apenas o nó de gatilho.",
  icone: "📋",
  checklist: [
    "Adicione os nós na paleta lateral e ligue as conexões.",
    "Configure no gatilho se ele dispara manualmente ou por palavra-chave.",
    "Clique em SALVAR antes de ativar.",
  ],
  nodes: [
    {
      id: "trigger-1",
      type: "trigger",
      position: { x: 80, y: 120 },
      data: { triggerType: "manual" },
    },
  ],
  edges: [],
};

// ------------------------------------------------------------
// 2) NICHO: Imobiliária
// ------------------------------------------------------------
const TEMPLATE_IMOBILIARIA = {
  key: "imobiliaria",
  nome: "Imobiliária",
  descricao:
    "Saudação, tipo de imóvel, faixa de preço, agendamento de visita e transferência para a equipe.",
  icone: "🏠",
  checklist: [
    "Selecione a fila de Corretores / Imóveis no nó 'Transferir para fila'.",
    "Confirme as etiquetas 'Lead Alto Potencial' e 'Em pesquisa' nos nós Aplicar etiqueta.",
    "Ajuste a lista de tipos de imóvel, preços e bairros conforme sua carteira.",
    "Revise os textos de saudação e despedida para o tom da sua imobiliária.",
    "Clique em SALVAR no canto superior direito antes de ativar.",
  ],
  tourSteps: [
    {
      title: "Bem-vindo ao fluxo Imobiliária! 🎉",
      description:
        "Calma que não precisa memorizar nada — eu vou te mostrar passo a passo (com destaque) o que precisa ser configurado para este fluxo funcionar perfeitamente no WhatsApp. Vamos lá?",
    },
    {
      title: "1. Dê um nome ao seu fluxo",
      selector: "[data-tour-target='flow-name-input']",
      placement: "bottom",
      description:
        "Escreva um nome fácil de encontrar depois (ex: Atendimento Imobiliário 2026, Boas-vindas leads novos).",
    },
    {
      title: "2. Personalize a saudação",
      nodeId: "imob-saudacao",
      placement: "bottom",
      description:
        "⭐ Painel lateral já está ABERTO → edite ali as mensagens de boas-vindas. Troque as frases pelo tom de voz da sua imobiliária. Quando terminar, clique em Próximo.",
    },
    {
      title: "3. Ajuste a lista de tipos de imóvel ✨",
      nodeId: "imob-menu-tipo",
      placement: "bottom",
      description:
        "Este é o segredo do 'mais bonico': uma LISTA WhatsApp nativa! Painel já abriu → coloque os tipos de imóvel que VOCÊ realmente atende. Cada opção pode ter uma descrição.",
    },
    {
      title: "4. Selecione a fila dos corretores",
      nodeId: "imob-transferir-fila",
      placement: "right",
      description:
        "🚨 PASSO MAIS IMPORTANTE: o painel de configuração já abriu → escolha a fila que realmente existe no seu sistema. Os clientes são transferidos para esta fila após a triagem.",
    },
    {
      title: "5. Quando terminar, clique em SALVAR 💾",
      selector: "[data-tour-target='save-btn']",
      placement: "bottom",
      description:
        "Sempre que terminar de editar os blocos, clique aqui em SALVAR. Depois, para este fluxo começar a atender clientes, volte para a lista e ative o switch 'Ativo'. Boas vendas! 🏡",
    },
  ],
  nodes: [
    {
      id: "imob-trigger",
      type: "trigger",
      position: { x: 80, y: 100 },
      data: { triggerType: "manual" },
    },
    {
      id: "imob-saudacao",
      type: "content",
      position: { x: 460, y: 100 },
      data: {
        items: [
          { kind: "delay", seconds: 1 },
          { kind: "text", value: "Olá, {{nome}}! 👋 Tudo bem?" },
          { kind: "delay", seconds: 2 },
          {
            kind: "text",
            value:
              "Sou o assistente da Imobiliária e vou ajudar você a encontrar o imóvel ideal. Vamos começar?",
          },
        ],
      },
    },
    {
      id: "imob-menu-tipo",
      type: "list",
      position: { x: 840, y: 100 },
      data: {
        message:
          "Primeiro me conta: qual tipo de imóvel você está procurando?",
        footer: "Equipe Imobiliária 🏡",
        listTitle: "Selecione o tipo de imóvel",
        buttonText: "Ver opções",
        options: [
          { number: "1", label: "Apartamento", description: "Residencial em prédio" },
          { number: "2", label: "Casa", description: "Residencial térrea/sobrado" },
          { number: "3", label: "Comercial / Salão", description: "Loja, sala, ponto" },
          { number: "4", label: "Terreno / Área", description: "Lote, fazenda, chácara" },
        ],
        invalidMessage:
          "Não entendi. Clique em 'Ver opções' e escolha um item, ou digite 1, 2, 3 ou 4.",
        maxAttempts: 3,
      },
    },
    {
      id: "imob-pergunta-faixa",
      type: "question",
      position: { x: 1220, y: 100 },
      data: {
        message:
          "Perfeito! Qual é a faixa de preço que você tem em mente? (ex: R$ 300.000 a R$ 500.000)",
        variable: "faixa_preco",
        answerType: "texto",
        invalidMessage: "",
      },
    },
    {
      id: "imob-pergunta-bairros",
      type: "question",
      position: { x: 1600, y: 100 },
      data: {
        message:
          "Quais bairros ou regiões você tem preferência? Pode mandar quantos quiser.",
        variable: "bairros_preferidos",
        answerType: "texto",
        invalidMessage: "",
      },
    },
    {
      id: "imob-buttons-visita",
      type: "buttons",
      position: { x: 1980, y: 100 },
      data: {
        message:
          "Ótimo! Já anotei tudo. Gostaria de agendar uma visita presencial ou virtual?",
        footer: "Escolha uma opção abaixo",
        options: [
          { number: "1", label: "Sim, quero agendar" },
          { number: "2", label: "Ainda não, quero só ver opções" },
        ],
        invalidMessage:
          "Não entendi. Clique em um dos botões ou digite 1 para Sim ou 2 para Não.",
        maxAttempts: 3,
      },
    },
    {
      id: "imob-pergunta-nome",
      type: "question",
      position: { x: 2360, y: 100 },
      data: {
        message:
          "Que legal! Para eu já deixar tudo organizado, qual seu nome completo?",
        variable: "nome_cliente",
        answerType: "texto",
        invalidMessage: "Por favor, digite seu nome.",
      },
    },
    {
      id: "imob-pergunta-telefone",
      type: "question",
      position: { x: 2740, y: 100 },
      data: {
        message: "E qual o melhor telefone/WhatsApp para entrarmos em contato?",
        variable: "telefone_cliente",
        answerType: "telefone",
        invalidMessage:
          "Preciso de um número com DDD. Pode mandar de novo? (ex: 11 99999-8888)",
      },
    },
    {
      id: "imob-tag-lead",
      type: "addTag",
      position: { x: 3120, y: 100 },
      data: {
        tagId: "",
        tagName: "Lead Imobiliária - Alto Potencial",
        acao: "adicionar",
        criarSeNaoExistir: true,
      },
    },
    {
      id: "imob-transferir-fila",
      type: "transferQueue",
      position: { x: 3500, y: 100 },
      data: { queueId: "", queueName: "Imóveis / Corretores" },
    },
    {
      id: "imob-nota-visitou",
      type: "note",
      position: { x: 3880, y: 100 },
      data: {
        note:
          "Lead vindo do fluxo Imobiliária.\n\n- Interesse: {{menu.label}}\n- Faixa de preço: {{faixa_preco}}\n- Bairros: {{bairros_preferidos}}\n- Nome: {{nome_cliente}}\n- Telefone: {{telefone_cliente}}\n- Quer visitar: Sim",
      },
    },
    {
      id: "imob-fim-sim",
      type: "end",
      position: { x: 4260, y: 100 },
      data: {
        message:
          "Agradecemos! Um de nossos corretores entrará em contato em breve para confirmar a visita. Até logo! 🏡",
        closeTicket: false,
      },
    },
    {
      id: "imob-set-var-interesse",
      type: "setVariable",
      position: { x: 2360, y: 420 },
      data: { variable: "interesse", value: "{{menu.label}} - {{faixa_preco}}" },
    },
    {
      id: "imob-tag-info",
      type: "addTag",
      position: { x: 2740, y: 420 },
      data: {
        tagId: "",
        tagName: "Lead Imobiliária - Em pesquisa",
        acao: "adicionar",
        criarSeNaoExistir: true,
      },
    },
    {
      id: "imob-nota-info",
      type: "note",
      position: { x: 3120, y: 420 },
      data: {
        note:
          "Lead vindo do fluxo Imobiliária (ainda não quer visitar).\n\n- Interesse: {{menu.label}}\n- Faixa de preço: {{faixa_preco}}\n- Bairros: {{bairros_preferidos}}\n- Variável interesse: {{interesse}}",
      },
    },
    {
      id: "imob-fim-nao",
      type: "end",
      position: { x: 3500, y: 420 },
      data: {
        message:
          "Tudo bem! Fico à disposição quando quiser ver mais opções ou agendar uma visita. Qualquer coisa é só chamar! 😊",
        closeTicket: false,
      },
    },
  ],
  edges: [
    { id: "e-imob-1", source: "imob-trigger", target: "imob-saudacao" },
    { id: "e-imob-2", source: "imob-saudacao", target: "imob-menu-tipo" },

    {
      id: "e-imob-3a",
      source: "imob-menu-tipo",
      sourceHandle: "opt-1",
      target: "imob-pergunta-faixa",
    },
    {
      id: "e-imob-3b",
      source: "imob-menu-tipo",
      sourceHandle: "opt-2",
      target: "imob-pergunta-faixa",
    },
    {
      id: "e-imob-3c",
      source: "imob-menu-tipo",
      sourceHandle: "opt-3",
      target: "imob-pergunta-bairros",
    },
    {
      id: "e-imob-3d",
      source: "imob-menu-tipo",
      sourceHandle: "opt-4",
      target: "imob-pergunta-bairros",
    },
    {
      id: "e-imob-3f",
      source: "imob-menu-tipo",
      sourceHandle: "fallback",
      target: "imob-pergunta-bairros",
    },

    { id: "e-imob-4a", source: "imob-pergunta-faixa", target: "imob-pergunta-bairros" },
    {
      id: "e-imob-4b",
      source: "imob-pergunta-bairros",
      target: "imob-buttons-visita",
    },

    {
      id: "e-imob-5a",
      source: "imob-buttons-visita",
      sourceHandle: "opt-1",
      target: "imob-pergunta-nome",
    },
    {
      id: "e-imob-5b",
      source: "imob-buttons-visita",
      sourceHandle: "opt-2",
      target: "imob-set-var-interesse",
    },
    {
      id: "e-imob-5f",
      source: "imob-buttons-visita",
      sourceHandle: "fallback",
      target: "imob-set-var-interesse",
    },

    { id: "e-imob-6a", source: "imob-pergunta-nome", target: "imob-pergunta-telefone" },
    { id: "e-imob-6b", source: "imob-pergunta-telefone", target: "imob-tag-lead" },
    { id: "e-imob-6c", source: "imob-tag-lead", target: "imob-transferir-fila" },
    { id: "e-imob-6d", source: "imob-transferir-fila", target: "imob-nota-visitou" },
    { id: "e-imob-6e", source: "imob-nota-visitou", target: "imob-fim-sim" },

    {
      id: "e-imob-7a",
      source: "imob-set-var-interesse",
      target: "imob-tag-info",
    },
    { id: "e-imob-7b", source: "imob-tag-info", target: "imob-nota-info" },
    { id: "e-imob-7c", source: "imob-nota-info", target: "imob-fim-nao" },
  ],
};

// ------------------------------------------------------------
// 3) NICHO: Suporte de Tecnologia
// ------------------------------------------------------------
const TEMPLATE_SUPORTE_TECNOLOGIA = {
  key: "suporte-tecnologia",
  nome: "Suporte de Tecnologia",
  descricao:
    "Triagem de problemas de TI, validação inicial, coleta de detalhes e transferência para a equipe técnica.",
  icone: "💻",
  checklist: [
    "Selecione a fila de TI / Suporte Técnico no nó 'Transferir para fila'.",
    "Confirme a etiqueta 'Chamado TI' no nó Aplicar etiqueta.",
    "Ajuste a lista com os tipos de problema que sua equipe resolve (ex: Wi-Fi, ERP, impressora).",
    "Ajuste o delay entre a pergunta e a transferência.",
    "Clique em SALVAR no canto superior direito antes de ativar.",
  ],
  tourSteps: [
    {
      title: "Bem-vindo ao fluxo Suporte de Tecnologia! 🎉",
      description:
        "Vou te mostrar passo a passo o que precisa ser configurado. São só 5 passos rápido, vai!",
    },
    {
      title: "1. Dê um nome ao seu fluxo",
      selector: "[data-tour-target='flow-name-input']",
      placement: "bottom",
      description:
        "Nomeie de forma fácil de encontrar depois (ex: Triagem TI, Suporte N1 WhatsApp).",
    },
    {
      title: "2. Personalize a saudação",
      nodeId: "supt-saudacao",
      placement: "bottom",
      description:
        "⭐ Painel lateral já está ABERTO → edite ali as mensagens iniciais com o tom da sua equipe de TI. Quando terminar, clique em Próximo.",
    },
    {
      title: "3. Ajuste a lista de tipos de problema",
      nodeId: "supt-list-problema",
      placement: "bottom",
      description:
        "Lista WhatsApp nativa! Painel já abriu → coloque os tipos de chamado que sua equipe realmente atende (Wi-Fi, ERP, impressora, e-mail, senha etc.).",
    },
    {
      title: "4. Selecione a fila de TI",
      nodeId: "supt-transferir-ti",
      placement: "right",
      description:
        "🚨 Passo CRUCIAL: o painel já abriu → escolha a fila do time técnico que realmente existe no sistema. Após a triagem, os chamados vão para esta fila.",
    },
    {
      title: "5. Clique em SALVAR para gravar 💾",
      selector: "[data-tour-target='save-btn']",
      placement: "bottom",
      description:
        "Depois que terminar tudo, salve! Volte para a lista e ative o switch 'Ativo' para começar a receber chamados. Sucesso! 💻",
    },
  ],
  nodes: [
    {
      id: "supt-trigger",
      type: "trigger",
      position: { x: 80, y: 100 },
      data: { triggerType: "manual" },
    },
    {
      id: "supt-digitando-1",
      type: "typing",
      position: { x: 460, y: 100 },
      data: { seconds: 2 },
    },
    {
      id: "supt-saudacao",
      type: "content",
      position: { x: 840, y: 100 },
      data: {
        items: [
          { kind: "delay", seconds: 1 },
          { kind: "text", value: "Olá, {{nome}}! 👋 Tudo bem?" },
          { kind: "delay", seconds: 1 },
          {
            kind: "text",
            value:
              "Sou o assistente de Tecnologia. Vou entender seu problema e te direcionar para o time técnico certo.",
          },
        ],
      },
    },
    {
      id: "supt-list-problema",
      type: "list",
      position: { x: 1220, y: 100 },
      data: {
        message: "Qual tipo de problema você está enfrentando?",
        footer: "Equipe de Tecnologia",
        listTitle: "Selecione o problema",
        buttonText: "Ver opções",
        options: [
          { number: "1", label: "Internet / Wi-Fi", description: "Conexão lenta ou sem acesso" },
          { number: "2", label: "Computador / Notebook", description: "Lentidão, travamentos, não liga" },
          { number: "3", label: "Impressora / Periféricos", description: "Não imprime, não conecta" },
          { number: "4", label: "Sistema / ERP / Software", description: "Erros no sistema, tela travada" },
        ],
        invalidMessage:
          "Não entendi. Clique em 'Ver opções' e escolha um item, ou digite 1, 2, 3 ou 4.",
        maxAttempts: 3,
      },
    },
    {
      id: "supt-buttons-reiniciou",
      type: "buttons",
      position: { x: 1600, y: 100 },
      data: {
        message:
          "Antes de encaminhar: você já tentou desligar e ligar de novo o dispositivo (reiniciar)?",
        footer: "Tente este passo rápido primeiro 😉",
        options: [
          { number: "1", label: "Sim, já reiniciei" },
          { number: "2", label: "Não, vou tentar agora" },
        ],
        invalidMessage:
          "Clique em um botão ou digite 1 (Sim) ou 2 (Não).",
        maxAttempts: 3,
      },
    },
    {
      id: "supt-pergunta-erro",
      type: "question",
      position: { x: 1980, y: 100 },
      data: {
        message:
          "Certo. Descreva em poucas palavras qual a mensagem de erro ou o que exatamente não está funcionando:",
        variable: "descricao_erro",
        answerType: "texto",
        invalidMessage: "",
      },
    },
    {
      id: "supt-delay",
      type: "delay",
      position: { x: 1980, y: 420 },
      data: { seconds: 15 },
    },
    {
      id: "supt-content-aguarde",
      type: "content",
      position: { x: 2360, y: 420 },
      data: {
        items: [
          { kind: "text", value: "Ok, tentei aguardar. Agora vamos continuar..." },
        ],
      },
    },
    {
      id: "supt-tag-prioridade",
      type: "addTag",
      position: { x: 2360, y: 100 },
      data: {
        tagId: "",
        tagName: "TI - Chamado aberto pelo fluxo",
        acao: "adicionar",
        criarSeNaoExistir: true,
      },
    },
    {
      id: "supt-transferir-ti",
      type: "transferQueue",
      position: { x: 2740, y: 100 },
      data: { queueId: "", queueName: "TI / Suporte Técnico" },
    },
    {
      id: "supt-nota-resumo",
      type: "note",
      position: { x: 3120, y: 100 },
      data: {
        note:
          "Chamado de TI gerado pelo fluxo.\n\n- Categoria do problema: {{menu.label}}\n- Descrição do erro: {{descricao_erro}}\n- (Ver histórico da conversa para saber se o cliente já reiniciou o dispositivo antes da triagem)",
      },
    },
    {
      id: "supt-fim",
      type: "end",
      position: { x: 3500, y: 100 },
      data: {
        message:
          "Pronto! Já anotei tudo e transferi para o time técnico. Em breve um especialista vai te atender. Agradecemos a paciência! 🛠️",
        closeTicket: false,
      },
    },
  ],
  edges: [
    { id: "e-supt-1", source: "supt-trigger", target: "supt-digitando-1" },
    { id: "e-supt-2", source: "supt-digitando-1", target: "supt-saudacao" },
    { id: "e-supt-3", source: "supt-saudacao", target: "supt-list-problema" },

    {
      id: "e-supt-4a",
      source: "supt-list-problema",
      sourceHandle: "opt-1",
      target: "supt-buttons-reiniciou",
    },
    {
      id: "e-supt-4b",
      source: "supt-list-problema",
      sourceHandle: "opt-2",
      target: "supt-buttons-reiniciou",
    },
    {
      id: "e-supt-4c",
      source: "supt-list-problema",
      sourceHandle: "opt-3",
      target: "supt-buttons-reiniciou",
    },
    {
      id: "e-supt-4d",
      source: "supt-list-problema",
      sourceHandle: "opt-4",
      target: "supt-buttons-reiniciou",
    },
    {
      id: "e-supt-4f",
      source: "supt-list-problema",
      sourceHandle: "fallback",
      target: "supt-buttons-reiniciou",
    },

    {
      id: "e-supt-5a",
      source: "supt-buttons-reiniciou",
      sourceHandle: "opt-1",
      target: "supt-pergunta-erro",
    },
    {
      id: "e-supt-5b",
      source: "supt-buttons-reiniciou",
      sourceHandle: "opt-2",
      target: "supt-delay",
    },
    {
      id: "e-supt-5f",
      source: "supt-buttons-reiniciou",
      sourceHandle: "fallback",
      target: "supt-pergunta-erro",
    },

    { id: "e-supt-6a", source: "supt-delay", target: "supt-content-aguarde" },
    { id: "e-supt-6b", source: "supt-content-aguarde", target: "supt-pergunta-erro" },

    { id: "e-supt-7a", source: "supt-pergunta-erro", target: "supt-tag-prioridade" },
    { id: "e-supt-7b", source: "supt-tag-prioridade", target: "supt-transferir-ti" },
    { id: "e-supt-7c", source: "supt-transferir-ti", target: "supt-nota-resumo" },
    { id: "e-supt-7d", source: "supt-nota-resumo", target: "supt-fim" },
  ],
};

// ------------------------------------------------------------
// 4) NICHO: Advocacia
// ------------------------------------------------------------
const TEMPLATE_ADVOCACIA = {
  key: "advocacia",
  nome: "Advocacia",
  descricao:
    "Triagem por área do direito, dados pessoais, resumo do caso e transferência para advogados.",
  icone: "⚖️",
  checklist: [
    "Selecione a fila de Triagem Jurídica / Advocacia no nó 'Transferir para fila'.",
    "Confirme a etiqueta de 'Novo Caso Jurídico' no nó Aplicar etiqueta.",
    "Ajuste a lista com as áreas do direito que seu escritório atende.",
    "Revise as perguntas de dados pessoais (CPF, e-mail etc.) para ficar em conformidade com a LGPD.",
    "Revise o texto da Nota interna que vai para o atendente (resumo do caso).",
    "Clique em SALVAR no canto superior direito antes de ativar.",
  ],
  tourSteps: [
    {
      title: "Bem-vindo ao fluxo Advocacia! 🎉",
      description:
        "Vou te guiar pelos pontos principais para este fluxo jurídico funcionar. São 6 passos rápidos, siga o foco amarelo!",
    },
    {
      title: "1. Dê um nome ao seu fluxo",
      selector: "[data-tour-target='flow-name-input']",
      placement: "bottom",
      description:
        "Nomeie de forma clara para você encontrar depois (ex: Triagem Jurídica, Atendimento Advocacia).",
    },
    {
      title: "2. Personalize a saudação",
      nodeId: "adv-saudacao",
      placement: "bottom",
      description:
        "⭐ Painel lateral já está ABERTO → edite ali as mensagens de boas-vindas com o tom formal/amigável do seu escritório. Quando terminar, clique em Próximo.",
    },
    {
      title: "3. Ajuste as áreas do direito ⚖️",
      nodeId: "adv-menu-area",
      placement: "bottom",
      description:
        "Lista WhatsApp nativa! Painel já abriu → coloque APENAS as áreas do direito que seu escritório atende. O cliente toca no botão e seleciona.",
    },
    {
      title: "4. Selecione a fila do escritório",
      nodeId: "adv-transferir-fila",
      placement: "right",
      description:
        "🚨 PASSO MAIS IMPORTANTE: painel já abriu → selecione a fila real de triagem jurídica ou dos advogados. Os clientes novos são transferidos para esta fila.",
    },
    {
      title: "5. Clique em SALVAR quando terminar 💾",
      selector: "[data-tour-target='save-btn']",
      placement: "bottom",
      description:
        "Quando terminar de personalizar tudo, salve. Lembre de ativar o fluxo na tela de listagem. SUCESSO no atendimento jurídico! ⚖️",
    },
  ],
  nodes: [
    {
      id: "adv-trigger",
      type: "trigger",
      position: { x: 80, y: 180 },
      data: { triggerType: "manual" },
    },
    {
      id: "adv-saudacao",
      type: "content",
      position: { x: 460, y: 180 },
      data: {
        items: [
          { kind: "delay", seconds: 1 },
          { kind: "text", value: "Olá, {{nome}}! 👋" },
          { kind: "delay", seconds: 1 },
          {
            kind: "text",
            value:
              "Seja bem-vindo(a) ao nosso atendimento jurídico. Para eu te direcionar ao advogado certo, vamos começar com algumas perguntas rápidas, ok?",
          },
        ],
      },
    },
    {
      id: "adv-menu-area",
      type: "list",
      position: { x: 840, y: 180 },
      data: {
        message:
          "Primeiro: qual área do direito seu caso se encaixa?",
        footer: "Escritório de Advocacia ⚖️",
        listTitle: "Selecione a área do direito",
        buttonText: "Ver opções",
        options: [
          { number: "1", label: "Trabalhista", description: "Horas extras, rescisões, acidentes" },
          { number: "2", label: "Cível / Consumidor", description: "Contratos, indenizações, consumidor" },
          { number: "3", label: "Previdenciário / INSS", description: "Aposentadorias, benefícios, pensão" },
          { number: "4", label: "Criminal", description: "Defesa em processos criminais" },
          { number: "5", label: "Família / Divórcio / Herança", description: "Divórcio, guarda, inventários, sucessões" },
          { number: "6", label: "Outra área", description: "Não sei qual se encaixa / outro caso" },
        ],
        invalidMessage:
          "Não entendi. Clique em 'Ver opções' e escolha um item, ou digite um número de 1 a 6.",
        maxAttempts: 3,
      },
    },
    {
      id: "adv-buttons-consulta",
      type: "buttons",
      position: { x: 1220, y: 180 },
      data: {
        message:
          "Entendido. Essa é a sua primeira consulta com nosso escritório?",
        footer: "Responderemos rapidamente após o cadastro",
        options: [
          { number: "1", label: "Sim, primeira vez" },
          { number: "2", label: "Não, já sou cliente" },
        ],
        invalidMessage:
          "Não entendi. Clique em um botão ou digite 1 (Sim) ou 2 (Não).",
        maxAttempts: 3,
      },
    },
    {
      id: "adv-pergunta-nome",
      type: "question",
      position: { x: 1600, y: 180 },
      data: {
        message: "Para começarmos, por favor, informe seu nome completo:",
        variable: "nome_completo",
        answerType: "texto",
        invalidMessage: "Por favor, digite seu nome completo.",
      },
    },
    {
      id: "adv-pergunta-cpf",
      type: "question",
      position: { x: 1980, y: 180 },
      data: {
        message: "E seu CPF (apenas números, sem pontos ou traço):",
        variable: "cpf_cliente",
        answerType: "numero",
        invalidMessage:
          "CPF deve conter apenas números (11 dígitos). Pode mandar de novo?",
      },
    },
    {
      id: "adv-pergunta-telefone",
      type: "question",
      position: { x: 2360, y: 180 },
      data: {
        message:
          "Qual o seu melhor telefone/WhatsApp para o advogado entrar em contato?",
        variable: "telefone_adv",
        answerType: "telefone",
        invalidMessage:
          "Preciso de um número com DDD. (ex: 11 99999-8888)",
      },
    },
    {
      id: "adv-pergunta-email",
      type: "question",
      position: { x: 2740, y: 180 },
      data: {
        message: "E seu e-mail para enviarmos documentos, se necessário:",
        variable: "email_adv",
        answerType: "email",
        invalidMessage:
          "Esse e-mail não parece certo. Pode conferir? (ex: nome@email.com)",
      },
    },
    {
      id: "adv-pergunta-resumo",
      type: "question",
      position: { x: 3120, y: 180 },
      data: {
        message:
          "Perfeito! Agora descreva brevemente seu caso: o que aconteceu, quando e o que você busca resolver:",
        variable: "resumo_caso",
        answerType: "texto",
        invalidMessage: "",
      },
    },
    {
      id: "adv-tag-caso",
      type: "addTag",
      position: { x: 3500, y: 180 },
      data: {
        tagId: "",
        tagName: "Jurídico - Nova triagem",
        acao: "adicionar",
        criarSeNaoExistir: true,
      },
    },
    {
      id: "adv-transferir-fila",
      type: "transferQueue",
      position: { x: 3880, y: 180 },
      data: { queueId: "", queueName: "Advocacia / Triagem Jurídica" },
    },
    {
      id: "adv-nota-resumo",
      type: "note",
      position: { x: 4260, y: 180 },
      data: {
        note:
          "Triagem jurídica do fluxo.\n\n- Área do direito: {{menu.label}}\n- Nome completo: {{nome_completo}}\n- CPF: {{cpf_cliente}}\n- Telefone: {{telefone_adv}}\n- E-mail: {{email_adv}}\n- (Ver histórico da conversa para confirmar se é a primeira consulta)\n\n----- RESUMO DO CASO -----\n{{resumo_caso}}",
      },
    },
    {
      id: "adv-fim",
      type: "end",
      position: { x: 4640, y: 180 },
      data: {
        message:
          "Agradecemos! Todos os dados foram enviados para nossa equipe jurídica. Um de nossos advogados analisará seu caso e entrará em contato em breve. Atenciosamente, escritório ⚖️.",
        closeTicket: false,
      },
    },
  ],
  edges: [
    { id: "e-adv-1", source: "adv-trigger", target: "adv-saudacao" },
    { id: "e-adv-2", source: "adv-saudacao", target: "adv-menu-area" },

    {
      id: "e-adv-3a",
      source: "adv-menu-area",
      sourceHandle: "opt-1",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3b",
      source: "adv-menu-area",
      sourceHandle: "opt-2",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3c",
      source: "adv-menu-area",
      sourceHandle: "opt-3",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3d",
      source: "adv-menu-area",
      sourceHandle: "opt-4",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3e",
      source: "adv-menu-area",
      sourceHandle: "opt-5",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3f",
      source: "adv-menu-area",
      sourceHandle: "opt-6",
      target: "adv-buttons-consulta",
    },
    {
      id: "e-adv-3fb",
      source: "adv-menu-area",
      sourceHandle: "fallback",
      target: "adv-buttons-consulta",
    },

    {
      id: "e-adv-4a",
      source: "adv-buttons-consulta",
      sourceHandle: "opt-1",
      target: "adv-pergunta-nome",
    },
    {
      id: "e-adv-4b",
      source: "adv-buttons-consulta",
      sourceHandle: "opt-2",
      target: "adv-pergunta-nome",
    },
    {
      id: "e-adv-4f",
      source: "adv-buttons-consulta",
      sourceHandle: "fallback",
      target: "adv-pergunta-nome",
    },

    { id: "e-adv-5a", source: "adv-pergunta-nome", target: "adv-pergunta-cpf" },
    { id: "e-adv-5b", source: "adv-pergunta-cpf", target: "adv-pergunta-telefone" },
    { id: "e-adv-5c", source: "adv-pergunta-telefone", target: "adv-pergunta-email" },
    { id: "e-adv-5d", source: "adv-pergunta-email", target: "adv-pergunta-resumo" },
    { id: "e-adv-6a", source: "adv-pergunta-resumo", target: "adv-tag-caso" },
    { id: "e-adv-6b", source: "adv-tag-caso", target: "adv-transferir-fila" },
    { id: "e-adv-6c", source: "adv-transferir-fila", target: "adv-nota-resumo" },
    { id: "e-adv-6d", source: "adv-nota-resumo", target: "adv-fim" },
  ],
};

// ============================================================
// EXPORT
// ============================================================
export const TEMPLATES = [
  TEMPLATE_BRANCO,
  TEMPLATE_IMOBILIARIA,
  TEMPLATE_SUPORTE_TECNOLOGIA,
  TEMPLATE_ADVOCACIA,
];

export const TEMPLATE_KEYS = {
  BRANCO: "branco",
  IMOBILIARIA: "imobiliaria",
  SUPORTE_TECNOLOGIA: "suporte-tecnologia",
  ADVOCACIA: "advocacia",
};

// Retorna o template pelo seu key (ou BRANCO, se não encontrar)
export const getTemplateByKey = (key) => {
  return TEMPLATES.find((t) => t.key === key) || TEMPLATE_BRANCO;
};
